import { apiRequest } from "./client";

export type TabOut = {
  id: string;
  name: string;
  slug: string;
  mssql_table_name: string;
  schema_valid: boolean;
  schema_last_checked: string | null;
  schema_report: Record<string, unknown> | null;
  qdrant_collection: string;
  read_only: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
  qdrant_point_count: number | null;
  mssql_conn_info: {
    server?: string | null;
    port?: number | null;
    database?: string | null;
    user?: string | null;
    driver?: string | null;
    trust_server_certificate?: boolean | null;
    encrypt?: string | null;
  } | null;
  azure_configured: boolean;
  azure_container_name: string | null;
  azure_valid: boolean | null;
  azure_last_checked: string | null;
  azure_report: Record<string, unknown> | null;
};

export type TabListResponse = { items: TabOut[] };

export type TabCredsIn = {
  server: string;
  port?: number | null;
  database: string;
  user: string;
  password: string;
  driver?: string | null;
  trust_server_certificate?: boolean;
  encrypt?: string | null;
};

export type TabAzureCredsIn = {
  connection_string: string;
  container_name: string;
};

export type TabCreateIn = {
  name: string;
  creds: TabCredsIn;
  mssql_table_name?: string;
  azure_creds?: TabAzureCredsIn | null;
};

export type TabUpdateIn = {
  name?: string;
  mssql_table_name?: string;
  creds?: TabCredsIn;
  azure_creds?: TabAzureCredsIn | null;
  clear_azure?: boolean;
};

export type TestConnectionResponse = {
  ok: boolean;
  schema_ok: boolean;
  schema_report: Record<string, unknown>;
  azure_ok: boolean | null;
  azure_error: string | null;
};

export type ValidateSchemaResponse = {
  schema_ok: boolean;
  schema_report: Record<string, unknown>;
};

export type ValidateAzureResponse = {
  azure_ok: boolean;
  azure_report: Record<string, unknown>;
};

export function listTabs() {
  return apiRequest<TabListResponse>("/admin/tabs");
}

export function getTab(tabId: string) {
  return apiRequest<TabOut>(`/admin/tabs/${tabId}`);
}

export function createTab(body: TabCreateIn) {
  return apiRequest<TabOut>("/admin/tabs", { method: "POST", body });
}

export function updateTab(tabId: string, body: TabUpdateIn) {
  return apiRequest<TabOut>(`/admin/tabs/${tabId}`, { method: "PATCH", body });
}

export function deleteTab(tabId: string) {
  return apiRequest<{ ok: boolean }>(`/admin/tabs/${tabId}`, { method: "DELETE" });
}

export function setReadOnly(tabId: string, readOnly: boolean) {
  return apiRequest<TabOut>(`/admin/tabs/${tabId}/read-only`, {
    method: "POST",
    body: { read_only: readOnly },
  });
}

export function testConnection(body: {
  creds: TabCredsIn;
  mssql_table_name?: string;
  azure_creds?: TabAzureCredsIn | null;
}) {
  return apiRequest<TestConnectionResponse>("/admin/tabs/test-connection", {
    method: "POST",
    body,
  });
}

/** Test-connection for an existing tab. Falls back to saved MSSQL / Azure
 * creds for any field not provided. Use this on the edit form so the operator
 * can test Azure without re-entering the MSSQL password. */
export function retestConnection(
  tabId: string,
  body: {
    creds?: TabCredsIn;
    mssql_table_name?: string;
    azure_creds?: TabAzureCredsIn | null;
  },
) {
  return apiRequest<TestConnectionResponse>(`/admin/tabs/${tabId}/test-connection`, {
    method: "POST",
    body,
  });
}

export function validateSchema(tabId: string) {
  return apiRequest<ValidateSchemaResponse>(`/admin/tabs/${tabId}/validate-schema`, {
    method: "POST",
  });
}

export function validateAzure(tabId: string) {
  return apiRequest<ValidateAzureResponse>(`/admin/tabs/${tabId}/validate-azure`, {
    method: "POST",
  });
}

export type RegeneratePendingResponse = {
  status: "started" | "no_pending";
  count: number;
};

export function regeneratePending(tabId: string) {
  return apiRequest<RegeneratePendingResponse>(
    `/admin/tabs/${tabId}/regenerate-pending`,
    { method: "POST" },
  );
}
